import React from "react";
import { Modal, Button, Alert, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { formatDateTime } from "./processingDisplay";
import { EntityRefBadge } from "../../components/EntityRef/EntityRefBadge";
import { formatEntityRef } from "../../lib/entityRef";
import type {
  DuplicateAction,
  DuplicateJobMatch,
  DuplicateTrackMatch,
  DuplicateUploadDetails,
} from "./processingTypes";
import styles from "./DuplicateUploadModal.module.scss";

// DRA-2 — duplicate-upload STOP gate UI (owner decisions 2026-07-23).
// Signal-specific outcomes from the API: BLOCK / STOP / REVIEW.
// Continue Anyway / allowDuplicate is retired — no override path.

function hasAction(actions: DuplicateAction[], action: DuplicateAction): boolean {
  return actions.includes(action);
}

function trackMatchReason(track: DuplicateTrackMatch): string {
  return track.isrcMatch
    ? "Same title & artist · ISRC match"
    : "Same title & artist";
}

function jobMatchLabel(job: DuplicateJobMatch): string {
  const name =
    job.title || job.originalFilename || formatEntityRef("processingJob", job.jobId);
  return job.artistName ? `${name} — ${job.artistName}` : name;
}

function humanizeEnum(value: string): string {
  return value
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function JobMatchStatus({ job }: { job: DuplicateJobMatch }): React.ReactElement {
  const result = humanizeEnum(job.decision || job.state);
  if (job.archived) {
    return (
      <Badge bg="dark">
        Archived{result ? ` • ${result}` : ""}
      </Badge>
    );
  }
  return (
    <Badge bg="light" text="dark">
      {result}
    </Badge>
  );
}

function ExistingSongCard({ song }: { song: DuplicateTrackMatch }): React.ReactElement {
  return (
    <section className={`mb-3 ${styles.existingSongCard}`}>
      <h6 className="mb-2">Existing Song</h6>
      <div>
        <strong>{song.title || formatEntityRef("track", song.trackId)}</strong>{" "}
        {song.playable ? (
          <Badge bg="success">Playable</Badge>
        ) : (
          <Badge bg="secondary">Not playable</Badge>
        )}
        {song.status ? (
          <Badge bg="light" text="dark" className="ms-1">
            {humanizeEnum(song.status)}
          </Badge>
        ) : null}
        {song.playbackVersionNumber ? (
          <Badge bg="light" text="dark" className="ms-1">
            v{song.playbackVersionNumber}
          </Badge>
        ) : null}
      </div>
      <div className="text-muted small mt-1">
        <EntityRefBadge kind="track" id={song.trackId} />
        {song.createdAt ? ` · created ${formatDateTime(song.createdAt)}` : ""}
      </div>
    </section>
  );
}

function JobMatchRow({
  job,
  onNavigate,
}: {
  job: DuplicateJobMatch;
  onNavigate: (path: string) => void;
}): React.ReactElement {
  return (
    <li className="d-flex flex-wrap align-items-center gap-2 py-2 border-bottom">
      <div className="flex-grow-1" style={{ minWidth: 200 }}>
        <div>
          <strong>{jobMatchLabel(job)}</strong>
        </div>
        <div className="small">
          <span className="text-muted">Status:</span> <JobMatchStatus job={job} />
        </div>
        <div className="text-muted small">
          Same checksum (exact file)
          {job.createdAt ? ` · uploaded ${formatDateTime(job.createdAt)}` : ""} ·{" "}
          <EntityRefBadge kind="processingJob" id={job.jobId} />
        </div>
      </div>
      <Button
        size="sm"
        variant="outline-primary"
        onClick={() =>
          onNavigate(`/processing-jobs/${encodeURIComponent(job.jobId)}`)
        }
      >
        View Song Processing Job
      </Button>
    </li>
  );
}

function modalTitle(details: DuplicateUploadDetails): string {
  if (details.outcome === "BLOCK") {
    return "Upload blocked — already processing";
  }
  if (details.outcome === "REVIEW") {
    return "Duplicate detection unavailable";
  }
  if (details.signal === "checksum-terminal-living-song") {
    return "Upload stopped — song already exists";
  }
  if (details.signal === "checksum-terminal-no-song") {
    return "Upload stopped — prior processing job";
  }
  if (details.signal === "active-artist-title") {
    return "Upload stopped — matching song identity";
  }
  if (details.reasonCode === "duplicate-override-retired") {
    return "Upload stopped — override not available";
  }
  return "Upload stopped — duplicate detected";
}

function primaryJobMatches(details: DuplicateUploadDetails): DuplicateJobMatch[] {
  if (details.outcome === "BLOCK") {
    return details.matches.checksumInFlight;
  }
  if (details.signal === "checksum-terminal-no-song") {
    return details.matches.checksum;
  }
  return [];
}

function resolveActionPath(
  action: DuplicateAction,
  details: DuplicateUploadDetails,
): string | null {
  if (action === "open-existing-song-processing") {
    const job = details.matches.checksumInFlight[0];
    return job
      ? `/processing-jobs/${encodeURIComponent(job.jobId)}`
      : null;
  }
  if (action === "open-prior-song-processing") {
    const job = details.matches.checksum[0];
    return job
      ? `/processing-jobs/${encodeURIComponent(job.jobId)}`
      : null;
  }
  if (action === "open-existing-song") {
    const trackId = details.existingSong?.trackId;
    return trackId ? `/songs?trackId=${encodeURIComponent(trackId)}` : null;
  }
  return null;
}

export function DuplicateUploadModal({
  show,
  fileName,
  details,
  onCancel,
  onReplaceSongAudio,
}: {
  show: boolean;
  fileName: string;
  details: DuplicateUploadDetails | null;
  onCancel: () => void;
  /** DRA-3 — open Replace Song Audio confirm when living Song + available. */
  onReplaceSongAudio?: () => void;
}): React.ReactElement {
  const navigate = useNavigate();

  const goTo = (path: string) => {
    onCancel();
    navigate(path);
  };

  if (!details) {
    return (
      <Modal show={show} onHide={onCancel} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Duplicate upload</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Duplicate details are unavailable.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCancel}>
            Cancel upload
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  const actions = details.actions;
  const jobMatches = primaryJobMatches(details);
  const showKeepSeparateNote = hasAction(
    actions,
    "keep-separate-after-title-change",
  );
  const replaceSongAudio = details.replaceSongAudio;
  const replaceAvailable = replaceSongAudio?.available === true;

  return (
    <Modal
      show={show}
      onHide={onCancel}
      size="lg"
      scrollable
      dialogClassName={styles.dialog}
      contentClassName={styles.content}
    >
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle(details)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          <strong>{fileName}</strong>
          {details.message ? ` — ${details.message}` : null}
        </p>

        {details.outcome === "BLOCK" ? (
          <Alert variant="danger" className="mb-3">
            This exact file is already mid-pipeline. Uploading it again would
            double-submit the same song. Open the in-flight Song Processing job
            below or cancel this upload.
          </Alert>
        ) : null}

        {details.outcome === "STOP" && details.signal === "checksum-terminal-living-song" ? (
          <Alert variant="warning" className="mb-3">
            This exact file already belongs to an existing Song. Open the Song,
            replace its audio, or cancel this upload.
          </Alert>
        ) : null}

        {details.outcome === "STOP" && details.signal === "checksum-terminal-no-song" ? (
          <Alert variant="warning" className="mb-3">
            This exact file matches a prior Song Processing job with no living
            Song. Open that job or cancel — do not create another Track silently.
          </Alert>
        ) : null}

        {details.outcome === "STOP" && details.signal === "active-artist-title" ? (
          <Alert variant="warning" className="mb-3">
            An Active Song already uses this Primary Artist + Title. Open the
            existing Song, replace its audio, or keep as a separate Song only
            after changing the title.
          </Alert>
        ) : null}

        {details.outcome === "REVIEW" ? (
          <Alert variant="warning" className="mb-3">
            <p className="mb-2">
              Duplicate detection is temporarily unavailable. Do not treat this
              as a clean allow — review before uploading again.
            </p>
            {details.warning?.productionPolicy ? (
              <p className="mb-1 small">{details.warning.productionPolicy}</p>
            ) : null}
            {details.warning?.errorName ? (
              <p className="mb-0 small text-muted">
                Error: {details.warning.errorName}
                {details.warning.stage ? ` · stage ${details.warning.stage}` : ""}
              </p>
            ) : null}
          </Alert>
        ) : null}

        {details.existingSong ? (
          <ExistingSongCard song={details.existingSong} />
        ) : null}

        {jobMatches.length > 0 ? (
          <section className="mb-3">
            <h6 className="mb-1">
              {details.outcome === "BLOCK"
                ? "In-flight Song Processing Job"
                : "Prior Song Processing Job"}
              {jobMatches.length > 1 ? "s" : ""}
            </h6>
            <ul className={styles.matchList}>
              {jobMatches.map((job) => (
                <JobMatchRow key={job.jobId} job={job} onNavigate={goTo} />
              ))}
            </ul>
          </section>
        ) : null}

        {showKeepSeparateNote ? (
          <p className="text-muted small mb-0">
            To keep this as a separate Song, change the title (and artist if
            needed) in the uploader, then upload again. Continue Anyway is not
            available for governed duplicates.
          </p>
        ) : null}

        {jobMatches.length > 0 && details.outcome !== "REVIEW" ? (
          <p className="text-muted small mb-0 mt-2">
            Archived Song Processing jobs still participate in duplicate detection.
            Only permanently deleted Song Processing jobs are removed from duplicate
            detection.
          </p>
        ) : null}
      </Modal.Body>
      <Modal.Footer className={styles.footer}>
        <div className="d-flex flex-wrap gap-2 ms-auto">
          {hasAction(actions, "open-existing-song-processing") ? (
            <Button
              variant="primary"
              onClick={() => {
                const path = resolveActionPath(
                  "open-existing-song-processing",
                  details,
                );
                if (path) goTo(path);
              }}
              disabled={!resolveActionPath("open-existing-song-processing", details)}
            >
              Open Song Processing
            </Button>
          ) : null}

          {hasAction(actions, "open-prior-song-processing") ? (
            <Button
              variant="primary"
              onClick={() => {
                const path = resolveActionPath("open-prior-song-processing", details);
                if (path) goTo(path);
              }}
              disabled={!resolveActionPath("open-prior-song-processing", details)}
            >
              Open Song Processing
            </Button>
          ) : null}

          {hasAction(actions, "open-existing-song") ? (
            <Button
              variant="primary"
              onClick={() => {
                const path = resolveActionPath("open-existing-song", details);
                if (path) goTo(path);
              }}
              disabled={!resolveActionPath("open-existing-song", details)}
            >
              Open Existing Song
            </Button>
          ) : null}

          {hasAction(actions, "replace-song-audio") ? (
            <div className={styles.replaceAction}>
              <Button
                variant="outline-primary"
                disabled={!replaceAvailable || !onReplaceSongAudio}
                onClick={() => onReplaceSongAudio?.()}
                data-testid="duplicate-replace-song-audio"
              >
                {replaceAvailable
                  ? "Replace Song Audio"
                  : "Replace Song Audio (unavailable)"}
              </Button>
              {!replaceAvailable && replaceSongAudio?.note ? (
                <div className="text-muted small mt-1">{replaceSongAudio.note}</div>
              ) : null}
            </div>
          ) : null}

          {hasAction(actions, "cancel-upload") || actions.length === 0 ? (
            <Button variant="secondary" onClick={onCancel}>
              Cancel upload
            </Button>
          ) : null}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
